import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Card, Form, Input, Table, Button, Typography, message, DatePicker } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { SmartBreadcrumbs } from '@/components/Breadcrumbs';
import { useAuthStore } from '@/store/authStore';
import { fetchAiAgentAudit, type AiAgentAuditRow } from '@/services/aiAgentAudit.service';
import { useUserDisplayResolver } from '@/hooks/useUserDisplayResolver';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const columns: ColumnsType<AiAgentAuditRow & { user_display: string }> = [
  { title: 'Time', dataIndex: 'created_at', width: 160, render: (v) => (v ? String(v).replace('T', ' ').slice(0, 19) : '—') },
  { title: 'User', dataIndex: 'user_display', width: 180, ellipsis: true },
  { title: 'Company', dataIndex: 'company_id', width: 100, ellipsis: true },
  { title: 'Module', dataIndex: 'module', width: 100, ellipsis: true },
  { title: 'Intent', dataIndex: 'intent', width: 100, ellipsis: true },
  { title: 'Message', dataIndex: 'user_message', ellipsis: true },
  { title: 'ms', dataIndex: 'duration_ms', width: 64, render: (v) => (v != null ? v : '—') },
  { title: 'Error', dataIndex: 'error', width: 120, ellipsis: true, render: (v) => v || '—' },
];

function escapeCsvCell(val: unknown): string {
  const s = val == null ? '' : String(val);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function rowsToCsv(data: AiAgentAuditRow[]): string {
  const header = [
    'created_at',
    'user_id',
    'company_id',
    'module',
    'intent',
    'user_message',
    'duration_ms',
    'error',
    'session_id',
    'page_context',
  ];
  const lines = [header.join(',')];
  for (const r of data) {
    lines.push(
      [
        r.created_at,
        r.user_id,
        r.company_id,
        r.module,
        r.intent,
        r.user_message,
        r.duration_ms,
        r.error,
        r.session_id,
        r.page_context,
      ]
        .map(escapeCsvCell)
        .join(',')
    );
  }
  return lines.join('\r\n');
}

function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

type FormVals = {
  companyId?: string;
  userId?: string;
  sessionId?: string;
  module?: string;
  dateRange?: [Dayjs, Dayjs] | null;
};

export const AiAgentAuditPage: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const { resolve: resolveUserDisplay } = useUserDisplayResolver();
  const [form] = Form.useForm<FormVals>();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState<AiAgentAuditRow[]>([]);
  const displayRows = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        user_display: resolveUserDisplay(r.user_id, 'Unknown user'),
      })),
    [rows, resolveUserDisplay]
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  const buildQueryParams = useCallback(
    (p: number, ps: number) => {
      const v = form.getFieldsValue() as FormVals;
      let from: string | undefined;
      let to: string | undefined;
      if (v.dateRange?.[0] && v.dateRange?.[1]) {
        from = v.dateRange[0].startOf('day').toISOString();
        to = v.dateRange[1].endOf('day').toISOString();
      }
      return {
        page: p,
        pageSize: ps,
        companyId: v.companyId?.trim() || undefined,
        sessionId: v.sessionId?.trim() || undefined,
        userId: v.userId?.trim() || undefined,
        module: v.module?.trim() || undefined,
        from,
        to,
      };
    },
    [form]
  );

  const load = useCallback(
    async (p: number, ps: number) => {
      if (!user?.is_super_admin) return;
      setLoading(true);
      try {
        const res = await fetchAiAgentAudit(buildQueryParams(p, ps));
        if (!res.success) {
          message.error('Failed to load audit log');
          return;
        }
        setRows(res.items || []);
        setTotal(res.total);
        setPage(res.page);
        setPageSize(res.pageSize);
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Request failed';
        message.error(msg);
      } finally {
        setLoading(false);
      }
    },
    [buildQueryParams, user?.is_super_admin]
  );

  const exportThisPage = useCallback(() => {
    if (!rows.length) {
      message.info('No rows to export');
      return;
    }
    const name = `ai-agent-audit-page-${page}.csv`;
    downloadCsv(name, rowsToCsv(rows));
  }, [rows, page]);

  const exportAllMatching = useCallback(async () => {
    if (!user?.is_super_admin) return;
    setExporting(true);
    try {
      const ps = 100;
      const maxPages = 100;
      const all: AiAgentAuditRow[] = [];
      const first = await fetchAiAgentAudit({ ...buildQueryParams(1, ps), page: 1, pageSize: ps });
      if (!first.success) {
        message.error('Export failed');
        return;
      }
      const totalToFetch = first.total;
      all.push(...(first.items || []));
      let p = 2;
      while (all.length < totalToFetch && p <= maxPages) {
        const res = await fetchAiAgentAudit({ ...buildQueryParams(p, ps), page: p, pageSize: ps });
        if (!res.success || !res.items?.length) break;
        all.push(...res.items);
        if (res.items.length < ps) break;
        p += 1;
      }
      const name = `ai-agent-audit-all-${dayjs().format('YYYY-MM-DD-HHmm')}.csv`;
      downloadCsv(name, rowsToCsv(all));
      message.success(`Exported ${all.length} row(s)`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Export failed';
      message.error(msg);
    } finally {
      setExporting(false);
    }
  }, [user?.is_super_admin, buildQueryParams]);

  useEffect(() => {
    if (user?.is_super_admin) {
      void load(1, 20);
    }
  }, [user?.is_super_admin, load]);

  if (!user?.is_super_admin) {
    return (
      <div style={{ padding: 24 }}>
        <SmartBreadcrumbs />
        <Alert
          type="warning"
          showIcon
          message="Access restricted"
          description="This page is only available to super administrators."
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <SmartBreadcrumbs />
      <Title level={3} style={{ marginTop: 0 }}>
        AI agent audit log
      </Title>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={() => void load(1, pageSize)}
          style={{ maxWidth: 1200 }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
            <Form.Item name="companyId" label="Company id" style={{ marginBottom: 0, minWidth: 180 }}>
              <Input allowClear placeholder="Filter" />
            </Form.Item>
            <Form.Item name="userId" label="User id" style={{ marginBottom: 0, minWidth: 180 }}>
              <Input allowClear placeholder="Filter" />
            </Form.Item>
            <Form.Item name="sessionId" label="Session" style={{ marginBottom: 0, minWidth: 200 }}>
              <Input allowClear placeholder="Filter" />
            </Form.Item>
            <Form.Item name="module" label="Module" style={{ marginBottom: 0, width: 140 }}>
              <Input allowClear placeholder="e.g. sales" />
            </Form.Item>
            <Form.Item name="dateRange" label="Date range" style={{ marginBottom: 0, minWidth: 280 }}>
              <RangePicker style={{ width: '100%' }} showTime allowClear format="YYYY-MM-DD HH:mm" />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" loading={loading}>
                Apply
              </Button>
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button onClick={exportThisPage} disabled={!rows.length || loading}>
                Export this page (CSV)
              </Button>
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button onClick={() => void exportAllMatching()} loading={exporting} disabled={loading || total === 0}>
                Export all matching (CSV, batched)
              </Button>
            </Form.Item>
          </div>
        </Form>
      </Card>
      <Table<AiAgentAuditRow & { user_display: string }>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={displayRows}
        scroll={{ x: 1200 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
            void load(p, ps);
          },
        }}
      />
    </div>
  );
};

export default AiAgentAuditPage;
